/**
 * Safe Area utilities for handling device notches, Dynamic Island, punch holes, etc.
 *
 * 규칙:
 * - 게임 씬(Canvas)은 전체 화면을 덮음 (status bar 영역 포함)
 * - UI 요소는 반드시 safe area 아래부터 배치
 * - iOS: 노치, Dynamic Island 영역 피함
 * - Android: 펀치홀, status bar 영역 피함
 * - 토스 WebView: 상단 "토스에서" 버튼 영역 피함
 */

import { Capacitor } from '@capacitor/core';

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// Cached safe area values
let cachedInsets: SafeAreaInsets | null = null;

/**
 * Check if running in AppsInToss (Toss WebView) environment
 */
export function isAppsInToss(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('toss') || ua.includes('appintoss')) {
    return true;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__APPS_IN_TOSS__ || (window as any).AppsInToss) {
    return true;
  }
  return false;
}

/**
 * Detect platform from User-Agent (works in WebView too)
 */
function detectPlatform(): 'ios' | 'android' | 'web' {
  // Capacitor native platform detection
  try {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      return platform;
    }
  } catch {
    // Capacitor not available (e.g., in Toss WebView)
  }

  // User-Agent based detection for WebView environments
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  }
  if (/android/.test(ua)) {
    return 'android';
  }
  return 'web';
}

/**
 * Get CSS env() safe area values
 */
function getCSSEnvSafeArea(): SafeAreaInsets {
  // Create a test element to measure actual env() values
  const testEl = document.createElement('div');
  testEl.style.cssText = `
    position: fixed;
    top: env(safe-area-inset-top, 0px);
    right: env(safe-area-inset-right, 0px);
    bottom: env(safe-area-inset-bottom, 0px);
    left: env(safe-area-inset-left, 0px);
    pointer-events: none;
    visibility: hidden;
  `;
  document.body.appendChild(testEl);

  const rect = testEl.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  const insets: SafeAreaInsets = {
    top: rect.top,
    right: windowWidth - rect.right,
    bottom: windowHeight - rect.bottom,
    left: rect.left,
  };

  document.body.removeChild(testEl);
  return insets;
}

/**
 * Initialize safe area insets (call once at app start)
 */
export async function initSafeArea(): Promise<SafeAreaInsets> {
  // Get CSS env values first (works on iOS Safari/WebView)
  const envInsets = getCSSEnvSafeArea();

  const insets: SafeAreaInsets = {
    top: envInsets.top,
    right: envInsets.right,
    bottom: envInsets.bottom,
    left: envInsets.left,
  };

  const platform = detectPlatform();

  // Apply minimum safe area for devices without proper env() support
  if (platform === 'ios' && insets.top === 0) {
    // iPhone X and later have notch/Dynamic Island (44px+)
    // Older iPhones have 20px status bar
    insets.top = 47; // Safe default for notch devices
  } else if (platform === 'android' && insets.top === 0) {
    // Android devices with punch hole/status bar (24-48px typically)
    insets.top = 48; // Safe default for most Android devices
  }

  // AppsInToss (Toss WebView) needs additional padding for Toss UI
  if (isAppsInToss()) {
    insets.top += 30; // Additional padding for Toss header area
    console.log('[SafeArea] AppsInToss detected, added 30px top padding');
  }

  // Cache the values
  cachedInsets = insets;

  // Update CSS variables for use in styles
  document.documentElement.style.setProperty('--safe-area-top', `${insets.top}px`);
  document.documentElement.style.setProperty('--safe-area-right', `${insets.right}px`);
  document.documentElement.style.setProperty('--safe-area-bottom', `${insets.bottom}px`);
  document.documentElement.style.setProperty('--safe-area-left', `${insets.left}px`);

  // Also set short-form aliases for compatibility
  document.documentElement.style.setProperty('--sat', `${insets.top}px`);
  document.documentElement.style.setProperty('--sar', `${insets.right}px`);
  document.documentElement.style.setProperty('--sab', `${insets.bottom}px`);
  document.documentElement.style.setProperty('--sal', `${insets.left}px`);

  // Set --safe-top/bottom for HTML styles that use these
  document.documentElement.style.setProperty('--safe-top', `${insets.top}px`);
  document.documentElement.style.setProperty('--safe-bottom', `${insets.bottom}px`);

  console.log(`[SafeArea] Platform: ${platform}, Insets:`, insets);

  return insets;
}

/**
 * Get current safe area insets (sync, uses cached values)
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  if (cachedInsets) {
    return { ...cachedInsets };
  }

  // Fallback to CSS values if not initialized
  const computedStyle = getComputedStyle(document.documentElement);
  return {
    top: parseFloat(computedStyle.getPropertyValue('--safe-area-top')) || 0,
    right: parseFloat(computedStyle.getPropertyValue('--safe-area-right')) || 0,
    bottom: parseFloat(computedStyle.getPropertyValue('--safe-area-bottom')) || 0,
    left: parseFloat(computedStyle.getPropertyValue('--safe-area-left')) || 0,
  };
}

/**
 * Check if the device has any safe area insets
 */
export function hasSafeAreaInsets(): boolean {
  const insets = getSafeAreaInsets();
  return insets.top > 0 || insets.right > 0 || insets.bottom > 0 || insets.left > 0;
}
